import { supabase } from './supabase';
import { logAudit } from '../hooks/useAudit';

/*
  The two reference downloads that lost their home, 6 September 2026.

  **Group F took three download buttons off the report screens**: Locations,
  Users and the whole Item Catalog. They were on all three count reports, so a
  person met the same three buttons three times over, and none of the three is a
  count report.

  **Where they belong is a question the owner has not answered.** He was given
  two reasonable answers on 4 September and picked neither: their own page under
  Reports, called something like "Lists to download", or on the screens that own
  them. This file takes the second, because it loses nothing today and moving
  two buttons later is trivial, and it is written down rather than done quietly:

  - **Locations was already covered.** Warehouses has "Export current", and that
    file carries the region, the city, the state, whether the location is do not
    use, AND the people assigned to it. So Locations simply came off.
  - **Users** is here, and Users & Access gained a button for it.
  - **The item catalog** is here, and the Item Catalog gained a button for it.

  **Settled 7 September 2026.** The owner's answer was to put them where they
  make the most sense, so this is now a decision rather than a holding position,
  and it is the same placement: **each list owns its own download.**

  The reasoning, written down so it is not reopened. A person looking for the
  list of people is on the people screen; a person looking for the catalog is on
  the catalog. **Warehouses already worked that way** and nobody has ever had to
  be told where its "Export current" is. A "Lists to download" page under Reports
  would be a fourth place to look for something that is already one press away on
  the screen that owns it, and it would leave every one of those screens without
  the button a person expects to find on it.

  **Locations stays off this list**, because Warehouses' own export already
  carries the region, the city, the state, whether the location is do not use,
  and the people assigned to it, which is more than the button being replaced
  ever did.
*/

/*
  **The job level comes from the site, not from the person.** Somebody can be an
  administrator at one company and a rep at another, so `profiles.role` is one
  answer to a question that has several. `site_users` is what the Users & Access
  screen itself lists: everybody in the site you are looking at, with the job
  level they hold HERE. A file that disagreed with that screen would be a file
  about nobody.

  **The Region column reads `user_regions`**, the list the Users & Access screen
  writes, and not the free text box that used to sit on a person's record. That
  box was removed on 5 September 2026 because it was a second answer to a
  question the database already answered properly, and the two disagreed.
*/
export async function usersCsv() {
  const [{ data, error }, { data: assigned }] = await Promise.all([
    supabase.rpc('site_users'),
    supabase.from('user_regions').select('user_id, region:regions(name)'),
  ]);
  if (error) return { rows: null, error };
  const byUser = {};
  for (const r of assigned || []) {
    if (!r.region?.name) continue;
    (byUser[r.user_id] = byUser[r.user_id] || []).push(r.region.name);
  }
  const header = ['Full Name', 'Email', 'Role', 'Region', 'Active'];
  const rows = (data || []).map(u => [
    u.full_name || '', u.email || '', u.role || '',
    (byUser[u.id] || []).sort().join(', '), u.is_active ? 'YES' : 'NO',
  ]);
  return { rows: [header, ...rows], count: rows.length, error: null };
}

export function recordExport(profile, action, count) {
  return logAudit(profile, action, 'export', { details: { count } });
}
